import React from 'react'
import { Alert, Table } from 'react-bootstrap'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import styled from 'styled-components'
import { checkSession } from '../../actions'
import { fetchESignDataByBusinessId } from '../../actions/e_sign'
import {
  E_SIGN_STATUS_ABORTED,
  E_SIGN_STATUS_AWAITING_SIGNATURE,
  E_SIGN_STATUS_COMPLETED_KYC,
  E_SIGN_STATUS_EXPIRED,
  E_SIGN_STATUS_SIGNED,
  E_SIGN_STATUS_SIGNED_KYC_COMPLETE,
  PAGINATION_RECORDS_PER_PAGE,
  SESSION_APP_CONTEXT,
  STORAGE_TYPE_LOCAL,
} from '../../config'
import StorageProviderFactory from '../../managers/storage_provider_factory'
import AppContext from '../../models/app_context'
import { formatTimeString } from '../../utils/formatters'
import { addToSearch, getActivePage, getSearchParams } from '../../utils/helpers'
import { CustomPagination } from '../common/custom_pagination/custom_pagination'
import { DivContentBox, DivTitleBox } from '../common/styled'
import Spinner3Bounce from '../spinner_3_bounce'
import StatusLabelESign from '../status_label_e_sign'
import ESignSearchBar from './esign-search-bar'

const LegendMap = {
  [E_SIGN_STATUS_AWAITING_SIGNATURE]: 'Form submitted. Waiting customer action to click on email and complete KYC',
  [E_SIGN_STATUS_COMPLETED_KYC]: 'KYC Complete. User still needs to perform Confirmation email step',
  [E_SIGN_STATUS_SIGNED]: 'Email confirmation Complete. User still needs to complete KYC',
  [E_SIGN_STATUS_SIGNED_KYC_COMPLETE]: 'Email confirmation complete, KYC complete',
  [E_SIGN_STATUS_ABORTED]: 'Customer aborted the process without completing any step',
  [E_SIGN_STATUS_EXPIRED]: 'Customer Email confirmation/KYC process expired due to timeout\n',
}

export const Legend = styled.div`
  display: flex;
  flex-flow: column;
`

export const LegendItem = styled.div`
  margin: 0.5rem;
`

class ESignGrid extends React.PureComponent {
  static recordsPerPage = 10

  static csvExportHeader = ['Code', 'Active', 'Children', 'Parent', 'Created', 'Updated']

  state = {
    filter: {
      search: '',
      status: null,
    },
    ordering: null,
    business: null,
  }

  constructor(props) {
    super(props)
    const { status, search, ordering } = getSearchParams()
    const filter = this.state.filter

    this.state = {
      filter: { ...filter, ...{ status, search: search || '' } },
      ordering,
      business: null,
    }
  }

  handleSelectPage = (page, metaKey) => {
    if (page === getActivePage(this.props.location)) {
      return
    }
    this.search(page, undefined, metaKey)
  }

  componentDidMount() {
    const { checkSession, fetchESignDataByBusinessId, location } = this.props
    checkSession()
    const ctx = StorageProviderFactory.create(STORAGE_TYPE_LOCAL).getItem(SESSION_APP_CONTEXT)
    if (ctx) {
      const business = new AppContext(JSON.parse(ctx)).getId()
      this.setState({ business })
      fetchESignDataByBusinessId(business, getActivePage(location), ESignGrid.recordsPerPage, this.state.filter)
    }
  }

  updateFilter = (filter) => this.setState({ filter })

  onSearch = (filter) => this.setState({ filter }, () => this.search(1))

  search = (page, sortData, metaKey) => {
    const { props, state } = this
    const { navigateToPage, fetchESignDataByBusinessId, navigateToPageInNewWindow } = props
    const { filter } = state
    let searchString = `?page=${page || getActivePage(props.location)}`
    const { search, status } = filter
    if (search) {
      searchString = addToSearch(searchString, `search=${search}`)
    }

    if (status && status !== 'All') {
      searchString = addToSearch(searchString, `status=${status}`)
    }

    if (sortData) {
      const { field, order } = sortData
      if (field && order) {
        searchString = addToSearch(searchString, `ordering=${order === 'asc' ? field : `-${field}`}`)
      }
    }
    if (metaKey) {
      navigateToPageInNewWindow('', searchString || '')
    } else {
      fetchESignDataByBusinessId(state.business, page, ESignGrid.recordsPerPage, filter, sortData)
      navigateToPage('', searchString || '')
    }
  }

  reset = () => {
    const { props, state } = this
    const { navigateToPage, fetchESignDataByBusinessId } = props
    fetchESignDataByBusinessId(state.business, 1, ESignGrid.recordsPerPage)
    navigateToPage('', '')
  }

  render() {
    const { state, props } = this
    const { isFetching, results, count, requestUrl, errorMessage, tableHeader } = props

    // const { data, isFetching, isFetchingUser, sortOrder, sortField, count, requestUrl } = props
    if (isFetching) {
      return (
        <div className='text-center grid-spinner-mt-240'>
          <Spinner3Bounce isActive />
        </div>
      )
    }

    return (
      <div className='ibox float-e-margins animated fadeIn section-shadow'>
        <DivTitleBox className='ibox-title'>
          <div className='row'>
            <ESignSearchBar
              filter={state.filter}
              updateFilter={this.updateFilter}
              onSearch={this.onSearch}
              onReset={(e) => this.reset()}
              count={count}
              requestUrl={requestUrl}
              header={ESignGrid.csvExportHeader}
            />
          </div>
        </DivTitleBox>
        <DivContentBox className='ibox-content table-responsive'>
          <div>
            {errorMessage && <Alert variant='danger'>{errorMessage}</Alert>}
            <Table striped bordered hover>
              <thead>
                <tr>
                  {tableHeader.map((entity, i) => (
                    <th key={i}>{entity}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results
                  .map((entity) => [
                    { key: 'code', value: entity.access_code.code },
                    {
                      key: 'transaction_id',
                      value: entity.transaction ? entity.transaction.id : null,
                    },
                    { key: 'badge', value: <StatusLabelESign state={entity.status} /> },
                    {
                      key: 'created',
                      value: formatTimeString(entity.access_code.created),
                    },
                  ])
                  .map((entity, i) => (
                    <tr key={i}>
                      {entity.map((obj, j) => {
                        const val = obj.value || 'N/A'
                        if (obj.key === 'transaction_id') {
                          if (obj.value) {
                            return (
                              <td key={j}>
                                <Link to={`/tx-details/${val}`}>{val}</Link>
                              </td>
                            )
                          }
                          return <td key={j}>{val}</td>
                        }
                        return <td key={j}>{val}</td>
                      })}
                    </tr>
                  ))}
              </tbody>
            </Table>
            <div className='text-center'>
              <CustomPagination
                activePage={getActivePage(props.location)}
                onPageChanged={this.handleSelectPage}
                totalRecords={count}
                pageLimit={PAGINATION_RECORDS_PER_PAGE}
              />
            </div>
          </div>
          {
            <Legend>
              {Object.keys(LegendMap).map((key, i) => (
                <LegendItem key={i}>
                  <StatusLabelESign state={key} /> - {LegendMap[key]}
                </LegendItem>
              ))}
            </Legend>
          }
        </DivContentBox>
      </div>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  errorMessage: state.eSign.errorMessage,
  results: state.eSign.results,
  isFetching: state.eSign.isFetching,
  count: state.eSign.count,
  nextUrl: state.eSign.nextUrl,
  pageHash: ownProps.location.hash,
  requestUrl: state.eSign.requestUrl,
})

const mapDispatchToProps = (dispatch, ownProps) =>
  bindActionCreators(
    {
      fetchESignDataByBusinessId,
      checkSession,
      navigateToPage: (p, s) => ownProps.history.push({ pathname: '/e-sign', search: s || '' }),
      navigateToPageInNewWindow: (p, s) => {
        window.open(`/e-sign${s}`, '_blank')
      },
    },
    dispatch
  )

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ESignGrid))
